import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  CircularProgress
} from '@mui/material';
import { DialogConfirm } from '../elements/DialogConfirm';
import { IconSortCustom } from '../elements/icons/IconSortCustom';
import { Icon_Filter } from '../../../assets';
import { PrimaryTableHeader } from '../elements/TableCustom';
import { Appendix } from 'types/report';
import InfiniteScroll from 'react-infinite-scroll-component';

export const HEADER_VIEW_ALL = [
  { label: 'Appendix', width: 120 },
  { label: 'PDF or Content' },
  { label: 'Actions', width: 120 }
];

type ModalGenerateReportProps = {
  isOpen: boolean;
  onCloseModal: () => void;
  onCancel?: () => void;
  handleFetchMore: () => void;
  hasMore: boolean;
  optionAsset: Appendix[];
  setIdDeleteAppendices: (value: number) => void;
  setItemEditAppendices: (value: Appendix) => void;
};

export const ModalViewAllAppendix = ({
  handleFetchMore,
  hasMore,
  optionAsset,
  onCancel,
  isOpen,
  setIdDeleteAppendices,
  setItemEditAppendices,
  onCloseModal
}: ModalGenerateReportProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <DialogConfirm maxWidth={781} isOpen={isOpen} onClose={onCloseModal}>
      <Container>
        <Header>
          <BackButton onClick={onCancel} role="button" tabIndex={0}>
            Back
          </BackButton>
          <Title variant="h1">All Appendices</Title>
        </Header>
        <Stack id={'id-table-clients'} sx={{ height: 'calc(100% - 20px)', overflow: 'scroll' }}>
          <InfiniteScroll
            dataLength={optionAsset.length}
            next={handleFetchMore}
            hasMore={hasMore}
            scrollableTarget={'id-table-clients'}
            loader={
              <Box p={1}>
                <CircularProgress />
              </Box>
            }>
            <TableContainer>
              <Table aria-label="appendices table">
                <TableHead>
                  <TableRow>
                    {HEADER_VIEW_ALL.map((header: PrimaryTableHeader, index: number) => {
                      return (
                        <StyledTableCell width={header.width} key={index}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            role="button"
                            tabIndex={0}>
                            {header.label}
                            {header.sort && (
                              <Stack
                                style={{
                                  alignItems: 'center',
                                  flexDirection: 'row',
                                  gap: '4px'
                                }}>
                                <IconSortCustom order={''} />
                                <Icon_Filter color={'#818181'} />
                              </Stack>
                            )}
                          </Box>
                        </StyledTableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {optionAsset?.map((row) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell>{`Appendix ${row.id}`}</StyledTableCell>
                      <StyledTableCell className="content-cell">
                        {row?.document?.storage?.fileName || row?.content}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ display: 'flex', gap: isMobile ? '6px' : '10px' }}>
                          <ActionButton onClick={() => setItemEditAppendices(row)} size="small">
                            <img
                              style={{ cursor: 'pointer' }}
                              src={'/icons/icon_action_edit.svg'}
                              alt={'icon_action_edit'}
                            />
                          </ActionButton>
                          <ActionButton onClick={() => setIdDeleteAppendices(row.id)} size="small">
                            <img
                              style={{ cursor: 'pointer' }}
                              src={'/icons/icon_action_delete.svg'}
                              alt={'icon_action_delete'}
                            />
                          </ActionButton>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        </Stack>
      </Container>
    </DialogConfirm>
  );
};

const Container = styled(Box)(({ theme }) => ({
  padding: '36px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  height: '466px',
  width: '781px',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
    height: 'auto',
    width: '90%',
    margin: '20px auto'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
    width: '95%'
  }
}));

const Header = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '24px'
});

const BackButton = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  fontSize: '16px',
  fontWeight: 700,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px'
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  flex: 1,
  fontSize: '24px',
  fontWeight: 700,
  textAlign: 'center',
  color: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 700,
  color: '#818181',
  borderBottom: '1px solid #818181',
  padding: '20px',
  '&.content-cell': {
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
    fontSize: '12px'
  }
}));

const StyledTableRow = styled(TableRow)({
  '&:last-child td': {
    borderBottom: '1px solid #f0f0f0'
  }
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: '4px',
  '&:hover': {
    backgroundColor: 'rgba(34, 106, 170, 0.04)'
  }
}));
